import type { SuburbPerformanceData, SuburbSeriesValues } from './types'

type AddressFields = {
  address_sub_number?: string | null
  address_lot_number?: string | null
  address_street_number?: string | null
  address_street?: string | null
  address_suburb?: string | null
  address_state?: string | null
  address_postcode?: string | null
}

export function formatAddress(listing: AddressFields): {
  line1: string
  line2: string
  full: string
} {
  const parts: string[] = []
  if (listing.address_sub_number) parts.push(listing.address_sub_number + '/')
  // Estate lots: "Lot 3, 1 Oakhill Estate" when a street number follows, plain
  // "Lot 11 Rodeo Drive" when it does not (land listings, 2026-09-03).
  if (listing.address_lot_number)
    parts.push('Lot ' + listing.address_lot_number + (listing.address_street_number ? ',' : ''))
  if (listing.address_street_number) parts.push(listing.address_street_number)
  if (listing.address_street) parts.push(listing.address_street)
  const line1 = parts.join(' ').replace('/ ', '/')

  const line2Parts: string[] = []
  if (listing.address_suburb) line2Parts.push(listing.address_suburb)
  if (listing.address_state) line2Parts.push(listing.address_state)
  if (listing.address_postcode) line2Parts.push(listing.address_postcode)
  const line2 = line2Parts.join(' ')

  return { line1, line2, full: [line1, line2].filter(Boolean).join(', ') }
}

/**
 * Sold listings show the SALE price, never the asking price (Sheree 2026-09-24).
 * Where the agency withheld it, say so: the asking price sits close enough to the
 * sale price to give away what the vendor wanted kept private (FeedBridge handoff
 * 2026-09-25).
 */
export function formatSoldPrice(listing: {
  sold_price?: number | null
  sold_price_display?: boolean | null
}): string {
  if (listing.sold_price_display === false) return 'Price Withheld'
  if (listing.sold_price) return `Sold for $${listing.sold_price.toLocaleString('en-AU')}`
  return 'Sold'
}

export function formatPrice(listing: {
  listing_vertical?: string | null
  price_display?: string | null
  price_view?: string | null
  rent_display?: string | null
  rent_value?: number | null
  rent_period?: string | null
  sold_price?: number | null
  sold_price_display?: boolean | null
  listing_status?: string | null
}): string {
  if (listing.listing_status === 'sold') return formatSoldPrice(listing)
  if (listing.listing_status === 'leased') {
    return 'Leased'
  }
  if (listing.listing_vertical === 'rental') {
    const disp = listing.rent_display?.trim()
    // Only trust rent_display if already formatted (has $ or words); a bare number must be reformatted.
    if (disp && /[$a-zA-Z]/.test(disp) && !/^(yes|no|true|false)$/i.test(disp)) return disp
    const amount =
      listing.rent_value ??
      (disp && /^\d[\d,]*$/.test(disp) ? Number(disp.replace(/,/g, '')) : null)
    if (amount) return `$${amount.toLocaleString()} per week`
    return 'Contact Agent'
  }
  // Filter out boolean-ish values like "yes"/"no" that aren't real price strings
  const display =
    listing.price_display && !/^(yes|no|true|false)$/i.test(listing.price_display)
      ? listing.price_display
      : null
  const view =
    listing.price_view && !/^(yes|no|true|false)$/i.test(listing.price_view)
      ? listing.price_view
      : null
  return display || view || 'Contact Agent'
}

export function getEffectiveStatus(listing: {
  listing_status?: string | null
  under_offer_value?: boolean | null
}): string {
  const isUnderOffer =
    listing.under_offer_value === true || listing.under_offer_value === ('true' as any)
  if (isUnderOffer && listing.listing_status === 'current') return 'under-contract'
  return listing.listing_status || 'current'
}

type AgentLike = { name?: string | null; email?: string | null; phones?: string[] | null }

/**
 * One entry per real agent. The Rex feed fills listingAgent slots 1 and 2 with
 * the same office contact when property management is both (16 Summerhill Rd,
 * 2026-09-03 — "WP Property Management" twice), sends empty slots (name and
 * email null), and repeats a number under two phone types. Keyed on name
 * (two people can share an office inbox; one person never has two names),
 * falling back to email; the first occurrence wins (Asana 1218072910882236).
 */
export function dedupeAgents<T extends AgentLike>(agents: T[] | null | undefined): T[] {
  if (!Array.isArray(agents)) return []
  const seen = new Set<string>()
  const out: T[] = []
  for (const agent of agents) {
    const email = agent?.email?.trim().toLowerCase() || ''
    const name = agent?.name?.trim().toLowerCase() || ''
    if (!email && !name) continue
    const key = name ? `name:${name}` : `email:${email}`
    if (seen.has(key)) continue
    seen.add(key)
    const phones = Array.isArray(agent.phones)
      ? agent.phones.filter(
          (phone, index, all) =>
            Boolean(phone?.trim()) &&
            all.findIndex((other) => other?.replace(/\D/g, '') === phone.replace(/\D/g, '')) ===
              index,
        )
      : agent.phones
    out.push(phones === agent.phones ? agent : { ...agent, phones })
  }
  return out
}

export function getFeatureCount(features: Record<string, unknown>, key: string): number {
  const val = features[key]
  if (typeof val === 'number') return val
  if (typeof val === 'string') {
    const num = parseInt(val, 10)
    return isNaN(num) ? 0 : num
  }
  return 0
}

/**
 * Card feature map from a listing detail. Land area lives on the detail's `listing`,
 * floor (building) area in kv.extraFields, and commercial car spaces in raw_json —
 * none of them on /api/listings summaries — so they're merged into the kv features
 * here; every card call site already fetches the detail for features/thumbnails.
 */
export function buildCardFeatures(detail: {
  listing: {
    land_area_value: number | null
    land_area_unit: string | null
    raw_json?: Record<string, unknown> | null
  }
  kv?: { features?: Record<string, unknown>; extraFields?: Record<string, unknown> } | null
}): Record<string, unknown> {
  const extra = (detail.kv?.extraFields || {}) as Record<string, { value?: unknown } | undefined>
  const floorRaw = extra.buildingArea?.value
  const floorParsed = floorRaw != null && floorRaw !== '' ? parseFloat(String(floorRaw)) : NaN
  const carRaw = (detail.listing.raw_json as Record<string, unknown> | null | undefined)?.carSpaces
  const carParsed =
    typeof carRaw === 'number' ? carRaw : carRaw ? parseInt(String(carRaw), 10) : NaN
  return {
    ...(detail.kv?.features || {}),
    landAreaValue: detail.listing.land_area_value,
    landAreaUnit: detail.listing.land_area_unit,
    floorAreaValue: Number.isFinite(floorParsed) ? floorParsed : null,
    floorAreaUnit: (extra.buildingAreaUnit?.value as string | undefined) ?? null,
    carSpaces: Number.isFinite(carParsed) ? carParsed : null,
  }
}

/**
 * True when a URL is already a CDN-resized WebP variant (`<hash>_<width>.webp`).
 *
 * Those can be served straight to the browser (`unoptimized`), skipping a redundant
 * next/image decode+resize+re-encode of an already-optimised file — and, because the
 * URL then matches what ImageGallery's `preloadAll` warms, letting slider "page turns"
 * hit browser cache. Anything else (a full-resolution original — which
 * getThumbnailUrls falls back to for sold listings that have no variants) keeps going
 * through next/image, so a card never loads a multi-megabyte photo.
 */
export function isPreSizedImage(url?: string | null): boolean {
  return !!url && /_\d{3,4}\.webp(?:[?#]|$)/i.test(url)
}

export function getResponsiveImageUrl(
  mediaId: string,
  originalUrl: string,
  resizedMedia: Record<string, { width: number; url: string }[]> | undefined,
  targetWidth: number,
): string {
  if (!resizedMedia?.[mediaId]) return originalUrl
  const variants = resizedMedia[mediaId]
  const match = variants.find((v) => v.width >= targetWidth) || variants[variants.length - 1]
  return match?.url || originalUrl
}

/**
 * Detail-page path for a specific listing: `/property/<address-slug>~<external_listing_id>`.
 * The id suffix exists because addresses are NOT unique in the feed — e.g. a sold sale
 * listing and a current rental can share one address, and the bare address slug can only
 * resolve to one of them. Links built from a card must open exactly the listing that was
 * clicked. Bare `/property/<address-slug>` URLs stay valid (shared links, sitemap) and
 * resolve via the slug map's live-listing-first rule.
 */
export function generateListingPath(
  listing: AddressFields & { external_listing_id?: string | null; slug?: string | null },
): string {
  // Clean URLs (Steve 2026-08-21): the mirror assigns every listing a slug that
  // is unique across the catalogue — unit number included, and a readable
  // "-sold-2019" tail only where one address genuinely holds several listings.
  // The ~id form remains for anything not yet mirrored, and old links still
  // resolve (the property page redirects them to the canonical URL).
  if (listing.slug) return `/property/${listing.slug}`

  const slug = generateAddressSlug(listing)
  return listing.external_listing_id
    ? `/property/${slug}~${listing.external_listing_id}`
    : `/property/${slug}`
}

/**
 * Address slug including the unit/sub number and the lot number, which the
 * plain one omits. Estate lots share one street address (six lots at
 * "1 Oakhill Estate"), so without the lot every one of them collided and fell
 * back to a "-current-2026-N" tail.
 */
export function generateAddressSlugWithUnit(
  listing: AddressFields & { address_sub_number?: string | null; address_lot_number?: string | null },
): string {
  const sub = listing.address_sub_number?.trim()
  const lot = listing.address_lot_number?.trim()
  let slug = generateAddressSlug(listing)
  if (sub) slug = `${sub.replace(/[^a-zA-Z0-9]/g, '')}-${slug}`
  if (lot) slug = `Lot-${lot.replace(/[^a-zA-Z0-9]/g, '')}-${slug}`
  return slug.replace(/-{2,}/g, '-').replace(/^-|-$/g, '')
}

/** Split a /property/[slug] param into its address slug and optional exact-listing id. */
export function parseListingSlug(slugParam: string): {
  addressSlug: string
  externalListingId: string | null
} {
  const tilde = slugParam.indexOf('~')
  if (tilde === -1) return { addressSlug: slugParam, externalListingId: null }
  return {
    addressSlug: slugParam.slice(0, tilde),
    externalListingId: slugParam.slice(tilde + 1) || null,
  }
}

export function generateAddressSlug(listing: AddressFields): string {
  const parts = [
    listing.address_street_number,
    listing.address_street,
    listing.address_suburb,
    listing.address_state,
    listing.address_postcode,
  ].filter(Boolean)

  return parts
    .join('-')
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9-]/g, '')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '')
}

// --- Inspection time parsing helpers ---

export interface InspectionTime {
  date: string
  startTime: string | null
  endTime: string | null
}

export function parseInspections(rawJson?: Record<string, unknown> | null): InspectionTime[] {
  if (!rawJson) return []

  const results: InspectionTime[] = []
  const inspectionTimesNode = rawJson.inspectionTimes as Record<string, unknown> | undefined
  let inspectionData: unknown[] = []

  if (inspectionTimesNode?.inspection) {
    const insp = inspectionTimesNode.inspection
    if (Array.isArray(insp)) {
      inspectionData = insp
    } else {
      inspectionData = [insp]
    }
  }

  // Fallback nodes
  if (inspectionData.length === 0) {
    const direct = rawJson.inspection || rawJson.inspections || rawJson.openTimes
    if (direct) {
      inspectionData = Array.isArray(direct) ? direct : [direct]
    }
  }

  for (const item of inspectionData) {
    if (typeof item === 'string') {
      const match = item.match(
        /(\d{1,2}-\w{3}-\d{4})\s+(\d{1,2}:\d{2}\s*(?:AM|PM)?)\s+to\s+(\d{1,2}:\d{2}\s*(?:AM|PM)?)/i,
      )
      if (match) {
        results.push({ date: match[1], startTime: match[2], endTime: match[3] })
      }
    } else if (item && typeof item === 'object') {
      const attrs = (item as Record<string, unknown>).$ as Record<string, string> | undefined
      if (attrs) {
        results.push({
          date: attrs.date || '',
          startTime: attrs.startTime || attrs.start || null,
          endTime: attrs.endTime || attrs.end || null,
        })
      }
    }
  }

  return results
}

/** Y/M/D from either inspection date form: "2026-09-05" or "05-Sep-2026". */
function parseInspectionDateParts(dateStr: string): { y: number; m: number; d: number } | null {
  const iso = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (iso) return { y: +iso[1], m: +iso[2], d: +iso[3] }
  const short = dateStr.match(/^(\d{1,2})-(\w{3})-(\d{4})$/)
  if (short) {
    const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']
    const m = months.indexOf(short[2].toLowerCase()) + 1
    if (m > 0) return { y: +short[3], m, d: +short[1] }
  }
  return null
}

/** "10:20am" / "1:05 PM" / "13:05" → minutes since midnight, or null. */
function parseClockMinutes(time: string | null): number | null {
  if (!time) return null
  const match = time.match(/(\d{1,2}):(\d{2})\s*(am|pm)?/i)
  if (!match) return null
  let h = parseInt(match[1], 10)
  const m = parseInt(match[2], 10)
  const ampm = match[3]?.toLowerCase()
  if (ampm === 'pm' && h < 12) h += 12
  if (ampm === 'am' && h === 12) h = 0
  return h * 60 + m
}

const LISTING_TIME_ZONE = 'Australia/Melbourne'

/** UTC offset (minutes) of the listing time zone at a given instant — handles DST. */
function listingZoneOffsetMinutes(at: Date): number {
  const name = new Intl.DateTimeFormat('en-US', {
    timeZone: LISTING_TIME_ZONE,
    timeZoneName: 'longOffset',
  })
    .formatToParts(at)
    .find((part) => part.type === 'timeZoneName')?.value
  const match = name?.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/)
  if (!match) return 600
  const sign = match[1] === '-' ? -1 : 1
  return sign * (parseInt(match[2], 10) * 60 + parseInt(match[3] || '0', 10))
}

/**
 * The instant an open time finishes, in real time. Inspection dates and times in
 * the feed are wall-clock Melbourne values with no zone, and the server runs in
 * UTC, so `new Date("2026-09-02T16:00")` would be ten hours out. A slot with no
 * time is treated as ending at the end of its day.
 */
export function inspectionEndsAt(insp: {
  date: string
  startTime: string | null
  endTime: string | null
}): Date | null {
  const parts = parseInspectionDateParts(insp.date)
  if (!parts) return null
  const minutes = parseClockMinutes(insp.endTime) ?? parseClockMinutes(insp.startTime) ?? 24 * 60 - 1
  // Resolve the zone offset at that calendar day (noon, to sit clear of DST edges).
  const approx = new Date(Date.UTC(parts.y, parts.m - 1, parts.d, 12))
  const offset = listingZoneOffsetMinutes(approx)
  return new Date(Date.UTC(parts.y, parts.m - 1, parts.d, 0, minutes) - offset * 60_000)
}

/**
 * Open times that have already finished. The Rex feed leaves old sessions in the
 * XML (measured 2026-09-03: 10 of 221 current listings carried August dates), and
 * a mirror row can hold a slot FeedBridge has since dropped, so the page must
 * filter at render time rather than trust the source (Asana 1218072910882236).
 */
export function isPastInspection(
  insp: { date: string; startTime: string | null; endTime: string | null },
  now: Date = new Date(),
): boolean {
  const ends = inspectionEndsAt(insp)
  // Unparseable → keep it; hiding real data on a parse miss is the worse failure.
  if (!ends) return false
  return ends.getTime() < now.getTime()
}

export function formatInspectionDate(dateStr: string): string {
  const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (isoMatch) {
    const d = new Date(`${dateStr}T00:00:00`)
    return d.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })
  }
  const shortMatch = dateStr.match(/^(\d{1,2})-(\w{3})-(\d{4})$/)
  if (shortMatch) {
    const d = new Date(`${shortMatch[2]} ${shortMatch[1]}, ${shortMatch[3]}`)
    if (!isNaN(d.getTime())) {
      return d.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })
    }
  }
  return dateStr
}

export function formatTime12h(time: string | null): string {
  if (!time) return ''
  if (/[ap]m/i.test(time)) return time.trim()
  const match = time.match(/^(\d{1,2}):(\d{2})$/)
  if (match) {
    let h = parseInt(match[1], 10)
    const m = match[2]
    const ampm = h >= 12 ? 'pm' : 'am'
    if (h > 12) h -= 12
    if (h === 0) h = 12
    return `${h}:${m} ${ampm}`
  }
  return time
}

/** Parse a date string (ISO or short format) into a Date object */
export function parseInspectionDateToDate(dateStr: string): Date | null {
  const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (isoMatch) return new Date(`${dateStr}T00:00:00`)
  const shortMatch = dateStr.match(/^(\d{1,2})-(\w{3})-(\d{4})$/)
  if (shortMatch) {
    const d = new Date(`${shortMatch[2]} ${shortMatch[1]}, ${shortMatch[3]}`)
    if (!isNaN(d.getTime())) return d
  }
  return null
}

// --- Suburb Profile Helpers ---

/** Get sorted series entries (oldest first = chronological) */
function getSortedEntries(data: SuburbPerformanceData | undefined) {
  if (!data?.series?.seriesInfo) return []
  return [...data.series.seriesInfo].sort((a, b) => a.year - b.year || a.month - b.month)
}

/** Extract the most recent value for a key from performance data */
export function getSeriesValue(
  data: SuburbPerformanceData | undefined,
  key: keyof SuburbSeriesValues,
  yearIndex: number = 0,
): number | null {
  if (!data?.series?.seriesInfo) return null
  // seriesInfo is sorted oldest-first from API, most recent is last
  const sorted = [...data.series.seriesInfo].sort((a, b) => b.year - a.year || b.month - a.month)
  const entry = sorted[yearIndex]
  if (!entry) return null
  return entry.values[key] ?? null
}

/** Get all annual values for a key (chronological — oldest first) */
export function getSeriesValues(
  data: SuburbPerformanceData | undefined,
  key: keyof SuburbSeriesValues,
): { year: number; value: number | null }[] {
  const entries = getSortedEntries(data)
  return entries.map((e) => ({ year: e.year, value: e.values[key] ?? null }))
}

/** Generate a suburb profile URL slug from suburb, state, postcode */
export function generateSuburbSlug(
  suburb: string,
  state: string,
  postcode?: string | null,
): string {
  const parts = [suburb, state]
  if (postcode) parts.push(postcode)
  return parts
    .join('-')
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-{2,}/g, '-')
}

/** Parse a suburb slug back into components */
export function parseSuburbSlug(
  slug: string,
): { suburb: string; state: string; postcode?: string } | null {
  // Expected format: suburb-name-VIC-3121 or suburb-name-VIC
  const stateMatch = slug.match(/^(.+)-(vic|nsw|qld|sa|wa|tas|nt|act)(?:-(\d{4}))?$/i)
  if (!stateMatch) return null

  const suburb = stateMatch[1]
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
  const state = stateMatch[2].toUpperCase()
  const postcode = stateMatch[3] || undefined

  return { suburb, state, postcode }
}

const CARD_THUMBNAIL_WIDTH = 600

export function getThumbnailUrl(detail: {
  media: { id: string; url: string; media_type: string }[]
  resizedMedia?: Record<string, { width: number; url: string }[]>
}): string | null {
  const primaryImage = detail.media?.find((m) => m.media_type === 'image')
  if (!primaryImage || !detail.resizedMedia?.[primaryImage.id]) return null
  const variants = detail.resizedMedia[primaryImage.id]
  const match =
    variants.find((v) => v.width >= CARD_THUMBNAIL_WIDTH) || variants[variants.length - 1]
  return match?.url || null
}

/** First `count` card-sized image URLs (Asana 022 card slider) */
export function getThumbnailUrls(
  detail: {
    media: { id: string; url: string; media_type: string }[]
    resizedMedia?: Record<string, { width: number; url: string }[]>
  },
  count = 3,
): string[] {
  const urls: string[] = []
  for (const m of detail.media || []) {
    if (m.media_type !== 'image') continue
    const variants = detail.resizedMedia?.[m.id]
    // SOLD listings carry full media but FeedBridge generates no resized variants
    // for them, so requiring variants left every sold card with a single image and
    // dead slider arrows. Fall back to the original URL — next/image resizes it at
    // the edge (fill + sizes + quality 60), which is already how slide 1
    // (listing.primary_image, also unresized) is served.
    const match = variants?.length
      ? variants.find((v) => v.width >= CARD_THUMBNAIL_WIDTH) || variants[variants.length - 1]
      : { url: m.url }
    if (match?.url) urls.push(match.url)
    if (urls.length >= count) break
  }
  return urls
}

export function buildImageSrcSet(
  mediaId: string,
  originalUrl: string,
  resizedMedia: Record<string, { width: number; url: string }[]> | undefined,
): string {
  if (!resizedMedia?.[mediaId]) return ''
  return resizedMedia[mediaId].map((v) => `${v.url} ${v.width}w`).join(', ')
}

/**
 * Virtual tour providers seen in `external_link`. The field is REAXML's generic
 * `<externalLink>`, so it also carries project sites, Airbnb pages and the odd
 * document — only a known tour host gets the `Virtual Tour` button.
 *
 * `embeddable` = the provider lets the tour load in an iframe, so it opens in
 * the on-page lightbox. Open2view sends `X-Frame-Options` on its property
 * pages, so it opens in a new tab instead (checked 2026-10-07).
 *
 * `currentOnly` = show the button on current listings only. Matterport takes
 * models down after a sale — all 13 Wilson links on sold listings open
 * "model not available" — so its tours are hidden once a listing is no longer
 * current (Steve 2026-10-07).
 */
const VIRTUAL_TOUR_HOSTS: { host: string; embeddable: boolean; currentOnly?: boolean }[] = [
  { host: 'ipropertyexpress.com', embeddable: true },
  { host: 'matterport.com', embeddable: true, currentOnly: true },
  { host: 'kuula.co', embeddable: true },
  { host: 'open2view.com', embeddable: false },
]

export interface VirtualTour {
  url: string
  embeddable: boolean
}

/** `status` is the listing's effective status (`getEffectiveStatus`). */
export function getVirtualTour(
  externalLink: string | null | undefined,
  status: string | null | undefined,
): VirtualTour | null {
  if (!externalLink) return null
  try {
    const url = new URL(externalLink.trim())
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null
    const host = url.hostname.toLowerCase()
    const match = VIRTUAL_TOUR_HOSTS.find((h) => host === h.host || host.endsWith(`.${h.host}`))
    if (!match) return null
    if (match.currentOnly && status !== 'current') return null
    // An http tour can't be framed on an https page — new tab for those.
    return { url: url.href, embeddable: match.embeddable && url.protocol === 'https:' }
  } catch {
    return null
  }
}
